import { Router, NavigationEnd } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  Observable,
  combineLatest,
  firstValueFrom,
  from,
  throwError
} from "rxjs";
import { filter, map, mergeMap, toArray } from "rxjs/operators";
import { assetUrl } from "@shared/helper/asset-url";
import { SidebarNavItem } from "@core/models";
import { KeycloakHelperService } from "./keycloak.helper.service";
import { FeatureToggleService } from "./feature-toggle.service";
import { FeatureTogglesModel } from "@core/models/feature-toggles";
import { FranchiseService } from "./franchise.service";
import {
  AppConfigService,
  ConfigurationPolicyCompleteDTO
} from "./app-config.service";

@Injectable({
  providedIn: "root"
})
export class NavigationService {
  private history: string[] = [];
  configPolicy: ConfigurationPolicyCompleteDTO;

  constructor(
    private http: HttpClient,
    private keycloakHelperService: KeycloakHelperService,
    private router: Router,
    private featureToggleService: FeatureToggleService,
    private franchiseService: FranchiseService,
    private configService: AppConfigService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.configPolicy = await firstValueFrom(this.configService.getConfig$());
  }

  canBackByLocation(): boolean {
    this.history.pop();
    return this.history.length > 0;
  }

  getHistory(): string[] {
    return this.history;
  }

  getNavigationItems(
    isFleetality: boolean,
    byModule?: string,
    lang?: string
  ): Observable<SidebarNavItem[]> {
    if (byModule === "parts-module" || byModule === "sales-module") {
      if (!lang) {
        return throwError(
          "NavigationService: lang must be provided for Part or Sales Module"
        );
      }

      const getFileName = (module: string, lang: string): string => {
        const langLower = lang.toLowerCase();
        const isPt = langLower === "pt";
        const modulePrefix =
          module === "sales-module" ? "navigation-sales" : "navigation";

        if (isPt) {
          return isFleetality
            ? `${modulePrefix}-fleetality.json`
            : `${modulePrefix}.json`;
        }

        return isFleetality
          ? `${modulePrefix}-${langLower}-fleetality.json`
          : `${modulePrefix}-${langLower}.json`;
      };

      const fileName = getFileName(byModule, lang);
      const url = assetUrl(`/mocks/${fileName}`);

      return this.http.get<SidebarNavItem[]>(url).pipe(
        mergeMap((items) => from(items)),
        map((item) => this.applyFilters(item, null, false)),
        filter((item) => item !== undefined),
        toArray()
      );
    }

    return combineLatest([
      this.featureToggleService.verifyFeatureToggle(),
      this.franchiseService.isDriveBFranchiseUser()
    ]).pipe(
      mergeMap(([toggles, isDriveBFranchiseUser]) => {
        const navConfigUrl = isFleetality
          ? assetUrl("/config/navigation_fleetality.json")
          : assetUrl("/config/navigation.json");

        return this.http.get<SidebarNavItem[]>(navConfigUrl).pipe(
          mergeMap((items) => from(items)),
          map((item) =>
            this.applyFilters(item, toggles, isDriveBFranchiseUser)
          ),
          filter((item) => item !== undefined),
          toArray()
        );
      })
    );
  }

  private applyFilters(
    item: SidebarNavItem,
    toggles: FeatureTogglesModel,
    isDriveBFranchiseUser: boolean
  ): SidebarNavItem {
    if (
      item.title === "part-module" &&
      item.url === "parts-module/service-manager" &&
      !isDriveBFranchiseUser
    ) {
      return;
    }
    if (
      item.title === "sales-module" &&
      item.url === "sales-module/contracts" &&
      !isDriveBFranchiseUser
    ) {
      return;
    }
    if (
      item.title === "maintenance-plans" &&
      item.url === "maintenance-plans" &&
      !isDriveBFranchiseUser
    ) {
      return;
    }

    if (item.title === "prices" && !isDriveBFranchiseUser) {
      return;
    }

    if (
      (item.url === "payment-statement" ||
        item.title === "payment-statement") &&
      !toggles.enablePaymentStatementMenu
    ) {
      return;
    }

    if (item.title === "database" && !isDriveBFranchiseUser) {
      return;
    }

    if (
      item.url === "reports" &&
      item.title === "request-report" &&
      !isDriveBFranchiseUser
    ) {
      return;
    }

    if (
      item.title === "home" &&
      item.url === "home" &&
      !isDriveBFranchiseUser
    ) {
      return;
    }
    if (
      item.title === "dashboard" &&
      item.url === "dashboard" &&
      !this.keycloakHelperService.isCarWorkshopLogged() &&
      isDriveBFranchiseUser
    ) {
      return;
    }

    if (
      item.url === "invoices" &&
      item.title === "invoices" &&
      this.keycloakHelperService.isFleetAdminLogged() &&
      (this.configPolicy?.configurationPolicy.driverFleetAdmViewPartInvoice ||
        this.configPolicy?.configurationPolicy.driverFleetAdmViewServiceInvoice)
    ) {
      return;
    }

    if (!this.hasPermission(item)) return;

    if (item.items) {
      let filteredChildren = item.items
        .map((child) =>
          this.applyFilters(child, toggles, isDriveBFranchiseUser)
        )
        .filter((child) => child !== undefined);

      if (filteredChildren.length === 0 && item.items.length > 0) {
        return;
      }

      return { ...item, items: filteredChildren };
    }

    return item;
  }

  private hasPermission(item: SidebarNavItem): boolean {
    return (
      this.hasRolePermission(item.roles) &&
      this.hasCountryPermission(item.countries)
    );
  }

  private hasRolePermission(roles: string[]): boolean {
    if (!roles) return true;
    return roles.includes(this.keycloakHelperService.getTypeUser());
  }

  private hasCountryPermission(countriesCodes: string[]): boolean {
    if (!countriesCodes) return true;
    return countriesCodes.includes(
      this.keycloakHelperService.getUserCountryCode()
    );
  }
}
